import React from 'react';
import { CheckCircleIcon, XCircleIcon, ClockIcon, UserIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useApprovalNotification } from '../contexts/ApprovalNotificationContext';

const GlobalApprovalModal: React.FC = () => {
  const {
    showApprovalModal,
    currentApproval,
    hideModal,
    approveApproval,
    rejectApproval,
    isLoading
  } = useApprovalNotification();

  if (!showApprovalModal || !currentApproval) {
    return null;
  }

  const handleApprove = async () => {
    try {
      await approveApproval(currentApproval.id);
    } catch (error) {
      console.error('Failed to approve automation:', error);
    }
  };

  const handleReject = async () => {
    try {
      await rejectApproval(currentApproval.id);
    } catch (error) {
      console.error('Failed to reject automation:', error);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const getPriorityColor = (priority: string) => {
    switch (priority?.toLowerCase()) {
      case 'high': return 'bg-red-100 text-red-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className={`fixed inset-0 z-50 overflow-y-auto ${showApprovalModal ? 'block' : 'hidden'}`}>
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div 
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={hideModal}
        ></div>

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          {/* Header */}
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <ExclamationTriangleIcon className="h-6 w-6 text-amber-500" />
                <h3 className="text-lg font-medium text-gray-900">
                  Automation Permission Required
                </h3>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                  <ClockIcon className="h-3 w-3 mr-1" />
                  Pending Approval
                </span>
              </div>
            </div>

            {/* Info alert */}
            <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-4">
              <p className="text-sm text-blue-800">
                A lead stage change has triggered an automation that requires your permission to execute.
              </p>
            </div>

            {/* Lead Information */}
            <div className="mb-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Lead Information</h4>
              <div className="flex items-center space-x-2">
                <UserIcon className="h-4 w-4 text-gray-500" />
                <span className="text-sm text-gray-600">Lead ID: {currentApproval.leadId}</span>
              </div>
            </div>

            {/* Task Details */}
            <div className="mb-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Task Details</h4>
              <div className="pl-4 space-y-2">
                <div>
                  <span className="text-sm font-medium text-gray-600">Task Name:</span>
                  <span className="ml-2 text-sm text-gray-900">{currentApproval.approvalData?.taskName || 'N/A'}</span>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-600">Description:</span>
                  <span className="ml-2 text-sm text-gray-900">{currentApproval.approvalData?.taskDescription || 'No description'}</span>
                </div>
                <div className="flex space-x-4">
                  <div>
                    <span className="text-sm font-medium text-gray-600">Priority:</span>
                    <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      getPriorityColor(currentApproval.approvalData?.priority || 'medium')
                    }`}>
                      {currentApproval.approvalData?.priority || 'Medium'}
                    </span>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-600">Category:</span>
                    <span className="ml-2 text-sm text-gray-900">{currentApproval.approvalData?.categoryName || 'N/A'}</span>
                  </div>
                </div>
                <div className="flex space-x-4">
                  <div>
                    <span className="text-sm font-medium text-gray-600">Assignee:</span>
                    <span className="ml-2 text-sm text-gray-900">{currentApproval.approvalData?.assigneeName || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-600">Supervisor:</span>
                    <span className="ml-2 text-sm text-gray-900">{currentApproval.approvalData?.supervisorName || 'N/A'}</span>
                  </div>
                </div>
                <div className="flex space-x-4">
                  <div>
                    <span className="text-sm font-medium text-gray-600">Start Date:</span>
                    <span className="ml-2 text-sm text-gray-900">
                      {currentApproval.approvalData?.startDateOffset ? `+${currentApproval.approvalData.startDateOffset} days` : 'Immediate'}
                    </span>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-600">Due Date:</span>
                    <span className="ml-2 text-sm text-gray-900">
                      {currentApproval.approvalData?.dueDateOffset ? `+${currentApproval.approvalData.dueDateOffset} days` : 'N/A'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Request Information */}
            <div className="mb-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Request Information</h4>
              <div className="pl-4 space-y-1">
                <div>
                  <span className="text-sm font-medium text-gray-600">Created:</span>
                  <span className="ml-2 text-sm text-gray-900">{formatDate(currentApproval.createdAt)}</span>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-600">Expires:</span>
                  <span className="ml-2 text-sm text-gray-900">{formatDate(currentApproval.expiresAt)}</span>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-600">Status:</span>
                  <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    currentApproval.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800'
                  }`}>
                    {currentApproval.status}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              onClick={handleApprove}
              disabled={isLoading}
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Processing...
                </div>
              ) : (
                <div className="flex items-center">
                  <CheckCircleIcon className="h-4 w-4 mr-2" />
                  Approve
                </div>
              )}
            </button>
            <button
              onClick={handleReject}
              disabled={isLoading}
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <XCircleIcon className="h-4 w-4 mr-2" />
              Reject
            </button>
            <button
              onClick={hideModal}
              disabled={isLoading}
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GlobalApprovalModal;
